import React, { type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { Container, Image } from 'react-bootstrap';
import style from './SearchResult.module.scss?module';
import Spinner1 from '../../../../Abstracts/Spinner1/Spinner1';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons/faSearch';
import OverflowedTextTooltip from '../../../../Abstracts/OverflowedTextTooltip/OverflowedTextTooltip';
import ListingRequestCta from '../../../../Abstracts/ListingRequest/ListingRequestCta/ListingRequestCta';
import clsx from 'clsx';

export interface Brand {
  id: number;
  name: string;
}

export interface Product {
  id: number;
  name: string;
  image_uri: string;
  image_overlay_uri: string | null;
  unit: string;
  unit_value: string;
  brand: Brand;
}

interface Props {
  brands: Brand[] | null;
  products: Product[] | null;
  setActive: React.Dispatch<SetStateAction<boolean>>;
  isFetching: boolean;
  handleSearchSubmit: () => void;
}

const SearchResult = ({
  brands,
  products,
  setActive,
  isFetching,
  handleSearchSubmit,
}: Props) => {
  const hasBrands = brands && brands.length > 0;
  const hasProducts = products && products.length > 0;

  return (
    <Container className={clsx(style.searchResult)}>
      {isFetching ? (
        <div className="d-flex justify-content-center align-items-center align-self-center w-100 h-100">
          <Spinner1 />
        </div>
      ) : !hasBrands && !hasProducts && !isFetching ? (
        <div className={style.noResults}>
          <div className="d-flex justify-content-center align-items-center w-100 flex-grow-1 gap-2">
            <i className="m-0">
              <FontAwesomeIcon size="sm" icon={faSearch} />
            </i>
            <p>No results found for your search.</p>
          </div>
          <ListingRequestCta
            source="search"
            className={style.listingRequestCta}
          />
        </div>
      ) : (
        <>
          <div className={style.searchResultBrands}>
            <h2>Brands</h2>
            <ul>
              {hasBrands ? (
                brands.map(({ id, name }) => (
                  <li key={id}>
                    <Link
                      className="text-decoration-none"
                      onClick={() => setActive(false)}
                      to={`/products?brand_ids=${id}`}
                    >
                      {name}
                    </Link>
                  </li>
                ))
              ) : (
                <li>No brands found</li>
              )}
            </ul>
          </div>
          <div className={style.searchResultProducts}>
            <h2>
              <span>Products</span>
              {hasProducts && (
                <button
                  className={style.viewAllBtn}
                  onClick={handleSearchSubmit}
                >
                  View all
                </button>
              )}
            </h2>
            <ul>
              {hasProducts ? (
                products.map(
                  ({ id, brand, name, image_uri, image_overlay_uri }) => (
                    <li key={id}>
                      <Link
                        className="text-decoration-none"
                        onClick={() => setActive(false)}
                        to={`/products?brand_ids=${brand.id}&product_size_ids=${id}`}
                      >
                        <div className={style.imageContainer}>
                          <Image src={image_uri} alt={name} loading="lazy" />
                          {image_overlay_uri && (
                            <div className={style.imageOverlay}>
                              <Image src={image_overlay_uri} fluid />
                            </div>
                          )}
                        </div>

                        <div className={style.textContainer}>
                          <strong>{brand.name}</strong>
                          <div className={style.productName}>
                            <OverflowedTextTooltip
                              text={name}
                              placement="bottom"
                            />
                          </div>
                        </div>
                      </Link>
                    </li>
                  )
                )
              ) : (
                <li>No Results found</li>
              )}
            </ul>
            {/* Below the results, regardless of the hit count (P-4972). */}
            <ListingRequestCta
              source="search"
              className={style.listingRequestCta}
            />
          </div>
        </>
      )}
    </Container>
  );
};

export default SearchResult;
